<template>
  <div class="el-main">
    <NewRirekishoEduHistoryCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
    />
    <NewRirekishoJobHistoryCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
    />
    <NewRirekishoLicenseCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
      :example-sentences="exampleSentences"
    />
    <NewRirekishoReasonCard
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
      :example-sentences="exampleSentences"
      :textarea-min-rows="textareaMinRows"
    />
    <NewRirekishoAiMyprCard
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
      :textarea-min-rows="textareaMinRows"
    />
    <NewRirekishoOthersCard
      :current-card-id="currentCardId"
      :format-id="formatId"
      :option-items="optionItems"
    />
    <NewRirekishoRequestsCard
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
      :example-sentences="exampleSentences"
    />
    <NewRirekishoCreateDateCard
      :current-card-id="currentCardId"
      :format-id="formatId"
      :is-send-yagi-post-enabled="isSendYagiPostEnabled"
    />

    <div class="card" v-show="currentCardId == 'confirm'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>作成した履歴書を確認</h2>
          <p>
            修正する場合は、プレビューの修正欄をクリックまたはステップを戻ることで修正することができます
          </p>
        </div>
      </div>
    </div>

    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="getMode != 'production1'"
    >
      <DownloadItem
        :formatId="formatId"
        :isSendYagiPostEnabled="isSendYagiPostEnabled"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
        :downloadActiveCollapses="downloadActiveCollapses"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import {
  createEmptyNewRirekishoFormatData,
  provideNewRirekishoFormatData,
  type NewRirekishoExampleSentences
} from '@/composables/format-edit/useNewRirekishoFormatData'
import {
  provideNewRirekishoHistories,
  useNewRirekishoHistories
} from '@/composables/format-edit/useNewRirekishoHistories'
import NewRirekishoEduHistoryCard from '@/components/format-edit/new-rirekisho/NewRirekishoEduHistoryCard.vue'
import NewRirekishoJobHistoryCard from '@/components/format-edit/new-rirekisho/NewRirekishoJobHistoryCard.vue'
import NewRirekishoLicenseCard from '@/components/format-edit/new-rirekisho/NewRirekishoLicenseCard.vue'
import NewRirekishoReasonCard from '@/components/format-edit/new-rirekisho/NewRirekishoReasonCard.vue'
import NewRirekishoAiMyprCard from '@/components/format-edit/new-rirekisho/NewRirekishoAiMyprCard.vue'
import NewRirekishoOthersCard from '@/components/format-edit/new-rirekisho/NewRirekishoOthersCard.vue'
import NewRirekishoRequestsCard from '@/components/format-edit/new-rirekisho/NewRirekishoRequestsCard.vue'
import NewRirekishoCreateDateCard from '@/components/format-edit/new-rirekisho/NewRirekishoCreateDateCard.vue'
import DownloadItem from '@/components/formats/CommonParts/DownloadItem.vue'

const props = defineProps<{
  basicData: Record<string, unknown>
  formatId?: string
  previewFormat?: {
    errorMessages?: Record<string, string>
    optionItems?: Record<string, unknown>
  } | null
  currentCardId?: string
  exampleSentences?: NewRirekishoExampleSentences
}>()

const { formLib } = useHostServices()

const { formatData } = useFormatLocalData({
  saveName: 'rirekisho',
  saveOtherName: 'shokumukeirekisho',
  createEmpty: createEmptyNewRirekishoFormatData
})
provideNewRirekishoFormatData(formatData)

const histories = useNewRirekishoHistories(formatData)
provideNewRirekishoHistories(histories)

const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)
const isSendYagiPostEnabled = ref(true)
const isSendYagiEmailEnabled = ref(true)
const downloadActiveCollapses = ref(['postcard'])

const previewErrors = computed(() => {
  if (!props.previewFormat || !props.previewFormat.errorMessages) return {}
  return props.previewFormat.errorMessages
})

const optionItems = computed(() => {
  if (!props.previewFormat || !props.previewFormat.optionItems) return {}
  return props.previewFormat.optionItems
})

const getMode = computed(() => process.env.VUE_APP_MODE)

const textareaMinRows = computed(() => {
  if (props.formatId === 'rirekisho_job_new') return 10
  if (props.formatId === 'rirekisho_job_new_single') return 6
  return 3
})

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

watch(
  () => formatData.createDate,
  (newValue) => {
    if (newValue == null) return
    formatData.writeDate = formLib.dateFormat(
      newValue as Date,
      'yyyy年 m月 d日'
    )
  }
)

onBeforeMount(() => {
  if (formatData.createDate === null) {
    formatData.createDate = formLib.getToday()
  }
  formatData.registYagioffer = true
  emitter.on('basic-data-changed', dataChanged)
  emitter.on('format-data-changed', dataChanged)
})

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
})

defineExpose({
  saveName: 'rirekisho',
  formatData,
  addEducationalHistory: histories.addEducationalHistory
})
</script>

<style lang="scss" scoped>
* {
  list-style: none;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

img {
  width: 100%;
  vertical-align: bottom;
}

.card-header h1,
.card-header h2 {
  padding-bottom: 1.5rem;
}
</style>
